import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  useListTrainingCoursesQuery,
  useCreateTrainingCourseMutation,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#b00020',
};

const STATUS_STYLE = {
  draft:           { bg: 'rgba(88,88,91,0.15)',   color: brand.charcoal,  label: 'Draft' },
  sandbox:         { bg: 'rgba(208,155,44,0.15)', color: brand.goldenrod, label: 'Sandbox' },
  pending_review:  { bg: 'rgba(176,111,0,0.18)',  color: '#b06f00',       label: 'Pending review' },
  published:       { bg: 'rgba(26,127,78,0.15)',  color: brand.green,     label: 'Published' },
  archived:        { bg: 'rgba(176,0,32,0.10)',   color: brand.red,       label: 'Archived' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span style={{
      background: s.bg,
      color: s.color,
      fontWeight: 700,
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '0.2rem 0.55rem',
      borderRadius: 999,
    }}>{s.label}</span>
  );
}
StatusBadge.propTypes = { status: PropTypes.string };

export default function TrainingCourses() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { data: courses = [], isLoading } = useListTrainingCoursesQuery();
  const [createCourse, { isLoading: isCreating }] = useCreateTrainingCourseMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newThreshold, setNewThreshold] = useState(80);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    setError(null);
    if (!newTitle.trim()) {
      setError('Title is required.');
      return;
    }
    try {
      const created = await createCourse({
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        pass_threshold: Number(newThreshold) || 80,
      }).unwrap();
      setShowCreate(false);
      setNewTitle('');
      setNewDescription('');
      setNewThreshold(80);
      navigate(`/compliance/training/${created.id}`);
    } catch (err) {
      setError(err?.data?.detail || 'Could not create course.');
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem 1rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '0.75rem' : '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: isMobile ? '0.5rem' : '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-mortarboard-fill" style={{ color: brand.goldenrod, fontSize: isMobile ? '1.4rem' : '1.75rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: isMobile ? '0.65rem' : '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 4px' }}>
              Compliance
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.15rem' : '1.75rem', lineHeight: 1.2 }}>Training Courses</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
              Build, publish, and manage compliance trainings end-to-end.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            style={{
              marginLeft: isMobile ? 0 : 'auto',
              background: brand.goldenrod,
              color: brand.deepBlue,
              border: 0,
              borderRadius: 8,
              padding: '0.55rem 1rem',
              fontWeight: 700,
              fontSize: '0.85rem',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <i className="bi bi-plus-lg me-1" /> New course
          </button>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div
          onClick={() => !isCreating && setShowCreate(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, padding: '1.5rem', maxWidth: 540, width: '100%', boxShadow: '0 10px 40px rgba(10,41,71,0.3)' }}
          >
            <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginBottom: '1rem' }}>Create new course</h5>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 4 }}>Title</label>
              <input
                type="text"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="e.g. 2026 Annual Compliance Training"
                style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem' }}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 4 }}>Description</label>
              <textarea
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                rows={3}
                placeholder="Brief summary shown to learners (optional)"
                style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem', resize: 'vertical' }}
              />
            </div>
            <div style={{ marginBottom: '1rem', maxWidth: 220 }}>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 4 }}>Pass threshold (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={newThreshold}
                onChange={e => setNewThreshold(e.target.value)}
                style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.95rem' }}
              />
            </div>
            {error && <div style={{ color: brand.red, fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                disabled={isCreating}
                style={{ background: 'transparent', color: brand.charcoal, border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 700 }}
              >
                {isCreating ? 'Creating…' : 'Create & edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course list */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : courses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: brand.charcoal }}>
            <i className="bi bi-mortarboard" style={{ fontSize: '2.5rem', color: brand.softGray, display: 'block', marginBottom: '0.5rem' }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No courses yet.</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Click <em>New course</em> to build your first training.</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {courses.map(c => (
              <li
                key={c.id}
                onClick={() => navigate(`/compliance/training/${c.id}`)}
                style={{
                  padding: isMobile ? '0.85rem 1rem' : '1rem 1.25rem',
                  borderBottom: `1px solid ${brand.softGray}`,
                  borderLeft: c.status === 'sandbox' ? `4px solid ${brand.goldenrod}`
                    : c.status === 'pending_review' ? `4px solid #b06f00`
                    : '4px solid transparent',
                  background: c.status === 'sandbox' ? 'rgba(208,155,44,0.06)'
                    : c.status === 'pending_review' ? 'rgba(176,111,0,0.06)'
                    : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.85rem',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.95rem' }}>{c.title}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  {c.description && (
                    <p style={{ margin: 0, fontSize: '0.825rem', color: brand.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.description}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: brand.charcoal, fontSize: '0.8rem', flexShrink: 0 }}>
                  <span title="Modules"><i className="bi bi-collection-play me-1" />{c.module_count || 0}</span>
                  <span title="Pass threshold"><i className="bi bi-bullseye me-1" />{c.pass_threshold}%</span>
                  <i className="bi bi-chevron-right" style={{ color: brand.charcoal }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
